/**
 * Stand-ins for the arcade boxes, for building and demoing the duel before the hardware exists. Open
 * `/duel?fakeboxes=2` (or `=1`) and the page sees that many boxes over a fake Web
 * Serial, exactly as it would see real ones (docs/serial-controller.md). A small panel at the bottom of the screen has
 * each box's three arcade buttons: click or tap one to press it, and it shows the lights the page asked the box for
 * (lit, blinking or off). "Unplug" pulls a box's cable. Reload to plug everything back in.
 *
 * Not gated to dev builds: it only wakes up for its own query parameter, and the panel's buttons are no faster than the
 * keyboard. Must run before the controller is first used, so main.tsx imports it ahead of the app.
 */
const BUTTONS = ['deny', 'build', 'approve'] as const;
type Button = (typeof BUTTONS)[number];
type Light = 'off' | 'on' | 'blink';

function install(count: number) {
  const ctls: (ReadableStreamDefaultController<Uint8Array> | undefined)[] = [];
  const say = (i: number, line: string) => ctls[i]?.enqueue(new TextEncoder().encode(`${line}\n`));
  const lights: Light[][] = Array.from({ length: count }, () => ['off', 'off', 'off']);
  const paint: (() => void)[] = [];

  const ports = Array.from({ length: count }, (_, i) => {
    let pending = '';
    const port = {
      readable: null as ReadableStream<Uint8Array> | null,
      // What the page writes to a box: `?` asks it to say hello, `leds …` sets the lights.
      writable: new WritableStream<Uint8Array>({
        write(chunk) {
          pending += new TextDecoder().decode(chunk);
          const lines = pending.split('\n');
          pending = lines.pop() ?? '';
          for (const line of lines) {
            if (line === '?') say(i, 'hello duel-box 2');
            const t = line.split(' ');
            if (t[0] === 'leds' && t.length === 4) {
              lights[i] = t.slice(1).map((l) => (l === 'on' || l === 'blink' ? l : 'off')) as Light[];
              paint[i]?.();
            }
          }
        },
      }),
      async open() {
        port.readable = new ReadableStream<Uint8Array>({ start: (c) => void (ctls[i] = c) });
      },
      async close() {},
    };
    return port;
  });
  Object.defineProperty(navigator, 'serial', {
    configurable: true,
    value: { getPorts: async () => ports, requestPort: async () => ports[0], addEventListener() {}, removeEventListener() {} },
  });

  const panel = document.createElement('div');
  panel.className = 'fake-boxes';
  panel.setAttribute('aria-label', 'Simulated arcade boxes');
  const mount = () => {
    for (let i = 0; i < count; i++) {
      const row = document.createElement('div');
      row.className = 'fake-box';
      const name = document.createElement('span');
      name.textContent = `Box ${i + 1}`;
      row.append(name);
      const btns = BUTTONS.map((b: Button, k) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = `arcade-btn ${b}`;
        el.setAttribute('aria-label', `Box ${i + 1} ${b}`);
        el.dataset.testid = `fake-box-${i + 1}-${b}`;
        const send = (down: boolean) => say(i, `btn ${b} ${down ? 'down' : 'up'}`);
        el.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          send(true);
        });
        for (const ev of ['pointerup', 'pointerleave', 'pointercancel'] as const) el.addEventListener(ev, () => send(false));
        row.append(el);
        return { el, k };
      });
      paint[i] = () => {
        for (const { el, k } of btns) el.dataset.light = lights[i][k];
      };
      paint[i]();
      const unplug = document.createElement('button');
      unplug.type = 'button';
      unplug.className = 'link-btn';
      unplug.textContent = 'Unplug';
      unplug.addEventListener('click', () => {
        ctls[i]?.close();
        row.classList.add('unplugged');
      });
      row.append(unplug);
      panel.append(row);
    }
    document.body.append(panel);
  };
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);
}

if (typeof window !== 'undefined') {
  const n = new URLSearchParams(window.location.search).get('fakeboxes');
  if (n !== null) install(Math.min(2, Math.max(1, Number(n) || 2)));
}
